import { remainingQuantity, deliveryProgress, purchaseOrderOperationalLabel, purchaseOrderLifecycleStage } from "../domain/models";
import {
  riskOf,
  daysUntil,
  number,
  remainingExplanation,
  date,
  nextAction,
  money,
} from "./purchase-order-presentation";
import { AlertTriangle, ChevronDown } from "lucide-react";
import type { PurchaseOrdersViewState } from "./purchase-order-view-state";
import type { PurchaseOrder } from "../domain/models";
import { PurchaseOrderDetails } from "./PurchaseOrderDetails";
type Props = Pick<PurchaseOrdersViewState,
  | "expanded"
  | "focusedOrderId"
  | "setExpanded"
  | "isPlanning"
  | "isSpecialist"
  | "isQuality"
  | "setQualityStatus"
  | "setQualityNote"
  | "setQualityDisposition"
  | "setOperationError"
  | "setDialog"
  | "documentUrl"
  | "submitting"
  | "setDispatchRecipient"
  | "setDeliveryAddress"
  | "openReceipt"
  | "setNote"> & { order: PurchaseOrder; };

export function PurchaseOrderRow({
  expanded,
  focusedOrderId,
  setExpanded,
  isPlanning,
  isSpecialist,
  isQuality,
  setQualityStatus,
  setQualityNote,
  setQualityDisposition,
  setOperationError,
  setDialog,
  documentUrl,
  submitting,
  setDispatchRecipient,
  setDeliveryAddress,
  openReceipt,
  setNote,
  order,
}: Props) {

  const open = expanded === order.id;
  const remaining = remainingQuantity(order);
  const progress = deliveryProgress(order);
  const isDraft = order.status === "CREATED";
  const risk = riskOf(order);
  const remainingDays = daysUntil(order.expectedDeliveryAt);
  const hasMeaningfulEvents =
    order.events.length > 1 ||
    order.events.some(
      (event) => event.eventType !== "PURCHASE_ORDER_CREATED",
    );
  return (
    <article
      key={order.id}
      data-purchase-order-id={order.id}
      className={`purchase-order-card status-${order.status.toLowerCase()} risk-${risk.toLowerCase()} ${focusedOrderId === order.id ? "is-focused" : ""}`.trim()}
    >
      <button
        className="purchase-order-card-summary"
        onClick={() => setExpanded(open ? null : order.id)}
        aria-expanded={open}
      >
        <span className="purchase-order-card-title">
          <span className="purchase-order-card-meta">
            <small>{order.orderNo}</small>
            <em>{purchaseOrderOperationalLabel(order)}</em>
          </span>
          <strong>{order.supplierName}</strong>
          <span>
            {order.requestTitle} · {order.requestNo}
          </span>
        </span>
        <span
          className="purchase-order-progress"
          data-label="Miktar / ilerleme"
        >
          {isDraft ? (
            <span>
              <strong>
                {number(order.orderedQuantity)} {order.unit}
              </strong>
            </span>
          ) : (
            <>
              <span>
                <strong>%{progress}</strong>
              </span>
              <i>
                <b style={{ width: `${progress}%` }} />
              </i>
              <span>
                <small>
                  {number(order.receivedQuantity)} teslim alındı
                </small>
                <small>
                  {number(remaining)} {order.unit} kaldı
                </small>
                {remainingExplanation(order) ? (
                  <small className="purchase-order-remaining-explanation">
                    {remainingExplanation(order)}
                  </small>
                ) : null}
              </span>
            </>
          )}
        </span>
        <span
          className="purchase-order-deadline"
          data-label="Planlanan teslimat"
        >
          <strong>{date(order.expectedDeliveryAt)}</strong>
          {order.overdue && remainingDays !== null ? (
            <em>
              <AlertTriangle size={13} /> {Math.abs(remainingDays)} gün
              gecikti
            </em>
          ) : !["RECEIVED", "CANCELLED"].includes(order.status) &&
            remainingDays !== null ? (
            <span>{remainingDays} gün kaldı</span>
          ) : null}
        </span>
        <span
          className="purchase-order-next"
          data-label={
            ["COMPLETED", "CANCELLED"].includes(
              purchaseOrderLifecycleStage(order),
            )
              ? "Sonuç"
              : "Sıradaki aksiyon"
          }
        >
          <strong>
            {isPlanning
              ? order.assignedSpecialistName
              : nextAction(order, isSpecialist, isQuality)}
          </strong>
        </span>
        {!isPlanning ? (
          <span
            className="purchase-order-amount"
            data-label="Sipariş tutarı"
          >
            <strong>{money(order.totalAmount, order.currency)}</strong>
          </span>
        ) : null}
        <ChevronDown size={18} className={open ? "is-open" : ""} />
      </button>

      {open ? (
        <PurchaseOrderDetails
          isPlanning={isPlanning}
          isQuality={isQuality}
          setQualityStatus={setQualityStatus}
          setQualityNote={setQualityNote}
          setQualityDisposition={setQualityDisposition}
          setOperationError={setOperationError}
          setDialog={setDialog}
          documentUrl={documentUrl}
          isSpecialist={isSpecialist}
          submitting={submitting}
          setDispatchRecipient={setDispatchRecipient}
          setDeliveryAddress={setDeliveryAddress}
          openReceipt={openReceipt}
          setNote={setNote}
          order={order}
          remaining={remaining}
          isDraft={isDraft}
          hasMeaningfulEvents={hasMeaningfulEvents}
        />
      ) : null}
    </article>
  );

}
